import {
  CompensationOrderLine,
  CompensationShippingFee,
  OrderCompensation,
  OrderLine,
  ShippingFee
} from "lib/types/generated/graphql-types"
import { OrderOperations } from "./OrderOperations"
import dayjs from "dayjs"

const DEPLOY_DATE = "2025-05-28:10:10"

export class Compensations extends OrderOperations {
  constructor(compensations: OrderCompensation[]) {
    super(compensations)
  }

  public isLegacyCompensation(date: string): boolean {
    return dayjs(date).isBefore(dayjs(DEPLOY_DATE))
  }

  public completedCompensations(): OrderCompensation[] {
    return this.completedList() as OrderCompensation[]
  }

  public pendingCompensations(): OrderCompensation[] {
    return this.pendingList() as OrderCompensation[]
  }

  public failedCompensations(): OrderCompensation[] {
    return this.failedList() as OrderCompensation[]
  }

  public getPendingCompensation(): OrderCompensation {
    const pendingList = this.pendingList() as OrderCompensation[]
    return pendingList[0]
  }

  public getOngoingCompensation(): OrderCompensation {
    const ongoingList = this.ongoingList() as OrderCompensation[]
    return ongoingList[0]
  }

  public getCompletedGiftCardAmount(id: string): number {
    return (
      this.completedCompensations()
        ?.flatMap((compensation) => compensation.giftCards)
        .find((compensatedGiftCard) => compensatedGiftCard?.giftCardId === id)?.amount ?? 0
    )
  }

  public paymentTotal(): number {
    return this.completedCompensations()
      .map((compensation) => compensation.totals.paymentTotal)
      .reduce((a, b) => a + b, 0)
  }

  public compensationFeeTotal(): number {
    return this.completedCompensations()
      .map((compensation) => compensation?.fee?.amount ?? 0)
      .reduce((a, b) => a + b, 0)
  }

  public orderLineTotal(orderLineId: string): number {
    const totalLegacyDiscount = this.completedCompensations()
      .filter((compensation) => this.isLegacyCompensation(compensation.created))
      .map((compensation) => compensation?.orderLines?.find((ol) => ol.orderLineId === orderLineId))
      .reduce((a, b) => a + (b?.totalDiscountAmount ?? 0), 0)

    const totalCompensations = this.completedCompensations()
      .filter((compensation) => !this.isLegacyCompensation(compensation.created))
      .map((compensation) => compensation?.orderLines?.find((ol) => ol.orderLineId === orderLineId))
      .reduce((a, b) => a + (b?.totalDiscountAmount ?? 0), 0)

    return totalCompensations + totalLegacyDiscount
  }

  public shippingFeeTotal(shippingFeeId: string, feeDiscount: number): number {
    const totalLegacyDiscount = this.completedCompensations()
      .filter((compensation) => this.isLegacyCompensation(compensation.created))
      .map((compensation) =>
        compensation?.shippingFees?.find((fee) => fee.shippingFeeId === shippingFeeId)
      )
      .filter((f) => !!f)
      .reduce((a, b) => a + b.totalDiscountAmount, 0)

    const totalShippingFees = this.completedCompensations()
      .filter((compensation) => !this.isLegacyCompensation(compensation.created))
      .map((compensation) =>
        compensation?.shippingFees?.find((fee) => fee.shippingFeeId === shippingFeeId)
      )
      .filter((f) => !!f)
      .reduce((a, b) => a + b.totalDiscountAmount, 0)

    return totalShippingFees + (totalLegacyDiscount - feeDiscount)
  }

  public giftCardTotal(): number {
    return this.completedCompensations()
      .map((compensation) => compensation.totals.giftCardTotal)
      .reduce((a, b) => a + b, 0)
  }

  public bonusTotal(): number {
    return this.completedCompensations()
      .map((compensation) => compensation.totals.bonusTotal)
      .reduce((a, b) => a + b, 0)
  }

  public getCompensationOrderLineAmount(
    compensationOrderLine: CompensationOrderLine,
    orderLine: OrderLine | undefined,
    date: string
  ): number {
    if (this.isLegacyCompensation(date)) {
      return compensationOrderLine.totalDiscountAmount - (orderLine?.discountAmount ?? 0)
    }
    return compensationOrderLine.totalDiscountAmount
  }

  public getCompensationShippingFeeAmount(
    compensationShippingfee: CompensationShippingFee,
    shippingFee: ShippingFee | undefined,
    date: string
  ): number {
    if (this.isLegacyCompensation(date)) {
      return compensationShippingfee.totalDiscountAmount - (shippingFee?.discountAmount ?? 0)
    }
    return compensationShippingfee.totalDiscountAmount
  }

  public getCompensationsOnOrderLine(orderLineId: string): OrderCompensation[] {
    return this.completedCompensations().filter((compensation) =>
      compensation.orderLines?.some((ol) => ol.orderLineId === orderLineId)
    )
  }
}
